import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Select,
  MenuItem,
  Typography,
  Chip,
  Divider,
} from "@mui/material";

import PanToolAltIcon from "@mui/icons-material/PanToolAlt";
import EditIcon from "@mui/icons-material/Edit";
import StopIcon from "@mui/icons-material/Stop";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteIcon from "@mui/icons-material/Delete";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import SaveIcon from "@mui/icons-material/Save";

import GeometryEditorCore from "./Editor";
import { useSnackbar } from '../../../context/SnackbarContext';
import ToolDialog from "../ToolDialog";
import FeatureAttributeForm from './ArtributeForm';

const  bulf_feature_update_url = 'https://app.mapgeoid.com/api-service/feature_service/bulk_feature_edit';

export default function GeometryEditor({ title, map, layers, onClose }) {
  const editorRef = useRef(null);
  const { showSnackbar } = useSnackbar();

  // Driven entirely by onStatusChange from core
  const [status, setStatus] = useState({
    editing: false,
    mode: "none",
    undo: 0,
    redo: 0,
    features: 0,
  });

  // commitData is the Map returned by core.commit()
  const [commitData, setCommitData] = useState(null);

  // stopGuard: user clicked Stop Edit but has unsaved changes
  const [stopGuard, setStopGuard] = useState(false);

  // Layer dropdown
  const [selectedLayerId, setSelectedLayerId] = useState("");
  const selectedLayer = layers[selectedLayerId] ?? null; // derived

  // for attribute form
  const [pendingFeature, setPendingFeature] = useState(null);


  // ── Init / re-init editor on layer change ──
  useEffect(() => {
    if (!map || selectedLayer === null) return;

    editorRef.current = new GeometryEditorCore(map, selectedLayer.vectorLayer, showSnackbar, {
      onDrawEnd: (feature) => setPendingFeature(feature),
      onStatusChange: (s) => setStatus({ ...s }),
      onCommit: (commits, payload) => setCommitData({ commits, payload }),
    });

    // FIX: call destroy() on cleanup so OL interactions are removed from map
    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
    };
  }, [selectedLayer]);

  // ── Toggle editing ──
  const handleToggleEdit = () => {
    if (!status.editing) {
      editorRef.current?.startEdit();
    } else {
      // Intercept stop if there are unsaved changes
      if (status.undo > 0) {
        setStopGuard(true);
      } else {
        editorRef.current?.stopEdit();
      }
    }
  };

  // ── Stop guard actions ──
  const handleGuardReviewAndCommit = () => {
    setStopGuard(false);
    editorRef.current?.commit(); // opens commit dialog via onCommit callback
  };

  const handleGuardDiscardAndStop = () => {
    setStopGuard(false);
    editorRef.current?.rollbackAll();
    editorRef.current?.stopEdit();
  };

  // ── Commit dialog actions ──
  const handleCommitConfirm = async () => {
    if (!commitData) return;
    const { commits, payload } = commitData;

    try {
      const res = await fetch(bulf_feature_update_url, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      editorRef.current?.confirmCommit();
      setCommitData(null);
      editorRef.current?.stopEdit();
      showSnackbar("Changes committed successfully", "success");
    } catch (err) {
      showSnackbar(`Commit failed: ${err.message}`, "error");
    }
  };


  const handleCommitRollback = () => {
    editorRef.current?.rollbackAll();
    setCommitData(null);
    editorRef.current?.stopEdit();
    showSnackbar("All changes rolled back", "info");
  };

  const handleCommitCancel = () => {
    // Just close dialog — stay in editing mode, stacks intact
    setCommitData(null);
  };

  // ── Helpers ──
  const stateChip = (state) => {
    if (state === "add") return <Chip label="Added" size="small" color="success" sx={{ fontSize: 10 }} />;
    if (state === "delete") return <Chip label="Deleted" size="small" color="error" sx={{ fontSize: 10 }} />;
    return <Chip label="Modified" size="small" color="warning" sx={{ fontSize: 10 }} />;
  };

  const modeLabel = !status.editing ? "Idle" : status.mode === "draw" ? "Drawing" : "Select";
  const modeColor = !status.editing ? "default" : status.mode === "draw" ? "warning" : "success";

  return (
    <>
      <ToolDialog title={title} open={true} onClose={onClose}>
        <div>

          {/* ── Layer Selector ── */}
          <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">Active Layer</Typography>
            <Select
              fullWidth
              value={selectedLayerId}
              onChange={(e) => setSelectedLayerId(e.target.value)}
              size="small"
              disabled={status.editing}
              MenuProps={{ style: { zIndex: 2000 } }}
              displayEmpty
              sx={{ mt: 0.5 }}
              renderValue={(value) => {
                if (!value) return <em style={{ color: "rgba(0,0,0,0.4)" }}>Select a layer</em>;
                return layers[value]?.name ?? value;
              }}
            >
              {Object.entries(layers).map(([layer_id, layer_info]) => (
                <MenuItem key={layer_id} value={layer_id}>
                  {layer_info.name}
                </MenuItem>
              ))}
            </Select>
            {status.editing && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                Stop editing to change layer
              </Typography>
            )}
          </Box>

          <Divider />

          {/* ── Toolbar ── */}
          <Box sx={{ display: "flex", flexWrap: "wrap", px: 1, py: 0.5, gap: 0.5, justifyContent: "center" }}>

            <Tooltip title={status.editing ? "Stop Edit" : "Start Edit"}>
              <IconButton onClick={handleToggleEdit} color={status.editing ? "error" : "default"} size="small">
                {status.editing ? <StopIcon /> : <EditIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Select / Modify">
              <span>
                <IconButton
                  onClick={() => editorRef.current?.enableSelectMode()}
                  disabled={!status.editing}
                  color={status.editing && status.mode === "select" ? "primary" : "default"}
                  size="small"
                >
                  <PanToolAltIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Draw Geometry">
              <span>
                <IconButton
                  onClick={() => editorRef.current?.enableDrawMode()}
                  disabled={!status.editing}
                  color={status.editing && status.mode === "draw" ? "primary" : "default"}
                  size="small"
                >
                  <AddLocationAltIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Delete Selected">
              <span>
                <IconButton
                  onClick={() => editorRef.current?.deleteSelected()}
                  disabled={!status.editing || status.mode !== "select"}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={`Undo (${status.undo})`}>
              <span>
                <IconButton
                  onClick={() => editorRef.current?.undo()}
                  disabled={status.undo === 0}
                  size="small"
                >
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={`Redo (${status.redo})`}>
              <span>
                <IconButton
                  onClick={() => editorRef.current?.redo()}
                  disabled={status.redo === 0}
                  size="small"
                >
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Commit Changes">
              <span>
                <IconButton
                  onClick={() => editorRef.current?.commit()}
                  disabled={!status.editing || status.undo === 0}
                  color="primary"
                  size="small"
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Divider />

          {/* ── Status Bar ── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, flexWrap: "wrap", background: "rgba(0,0,0,0.02)" }}>
            <Chip label={modeLabel} size="small" color={modeColor} sx={{ fontSize: 10, height: 20 }} />
            <Typography variant="caption" color="text.secondary">
              Features: <strong>{status.features}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Undo: <strong>{status.undo}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Redo: <strong>{status.redo}</strong>
            </Typography>
            {status.undo > 0 && (
              <Typography variant="caption" color="warning.main" fontWeight={500}>
                Unsaved changes
              </Typography>
            )}
          </Box>
        </div>
      </ToolDialog>

      {/* attribute form  */}
      <Dialog
        open={!!pendingFeature}
        onClose={() => setPendingFeature(null)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 3000 }}
      >
        <DialogTitle>Feature Attributes</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FeatureAttributeForm
            feature={pendingFeature}
            schema={selectedLayer?.property_schema ?? []}
            onSubmit={(attrMap) => {
              pendingFeature.setProperties(attrMap);
              setPendingFeature(null);
            }}
            onCancel={() => {
              setPendingFeature(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ══ STOP EDIT GUARD ══ */}
      <Dialog open={stopGuard} onClose={() => setStopGuard(false)} maxWidth="xs" fullWidth sx={{ zIndex: 3000 }}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You have <strong>{status.undo}</strong> unsaved change{status.undo !== 1 ? "s" : ""}.
            What would you like to do?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopGuard(false)} size="small">
            Stay in Edit
          </Button>
          <Button onClick={handleGuardDiscardAndStop} color="error" variant="outlined" size="small">
            Discard &amp; Stop
          </Button>
          <Button onClick={handleGuardReviewAndCommit} color="primary" variant="contained" size="small">
            Review &amp; Commit
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ COMMIT SUMMARY DIALOG ══ */}
      <Dialog open={!!commitData} onClose={handleCommitCancel} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}>
        <DialogTitle>
          Commit Changes
          <Typography variant="caption" color="text.secondary" display="block">
            {commitData?.commits?.size ?? 0} feature{(commitData?.commits?.size ?? 0) !== 1 ? "s" : ""} affected
          </Typography>
        </DialogTitle>
        <DialogContent>
          {commitData && commitData.commits?.size > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Feature ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Origin</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from(commitData.commits.entries()).map(([id, commit]) => (
                  <TableRow key={id}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                      {String(id)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={commit.type === "NEW" ? "New" : "Existing"}
                        size="small"
                        variant="outlined"
                        color={commit.type === "NEW" ? "primary" : "default"}
                        sx={{ fontSize: 10 }}
                      />
                    </TableCell>
                    <TableCell>{stateChip(commit.state)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">No changes to commit.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCommitCancel} size="small">Cancel</Button>
          <Button onClick={handleCommitRollback} color="error" variant="outlined" size="small">
            Rollback All
          </Button>
          <Button
            onClick={handleCommitConfirm}
            color="primary"
            variant="contained"
            size="small"
            disabled={!commitData || commitData.commits?.size === 0}
          >
            Commit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}